import React, { useState, useRef } from 'react';
import Modal from '../../../../../components/common/Modal';
import Button from '../../../../../components/common/Button/Button';
import Input from '../../../../../components/common/Input/Input';
import { translate } from '../../../../../utils/Common';
import * as FaIcons from 'react-icons/fa';
import { FiUpload, FiImage, FiBriefcase, FiUsers } from 'react-icons/fi';
import styles from './CreateCompanyModal.module.css';

const CreateCompanyModal = ({ isOpen, onClose, onCreateSuccess }) => {
  const [formData, setFormData] = useState({
    companyCode: '',
    companyName: '',
    contactPrimaryEmail: '',
    contactPrimaryFirstName: '',
    contactPrimaryLastName: '',
    lockerConsolidation: false
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, logo: translate('dashboard.companies.modal.create.logo.errors.invalidFileType') }));
      return;
    }

    // Validate file size (max 3MB)
    const maxSize = 3 * 1024 * 1024; // 3MB
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, logo: translate('dashboard.companies.modal.create.logo.errors.fileTooLarge') }));
      return;
    }

    setErrors(prev => ({ ...prev, logo: '' }));
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.onerror = () => {
      setErrors(prev => ({ ...prev, logo: translate('dashboard.companies.modal.create.logo.errors.readError') }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setErrors(prev => ({ ...prev, logo: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyCode.trim()) {
      newErrors.companyCode = translate('dashboard.companies.modal.create.validation.companyCodeRequired');
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = translate('dashboard.companies.modal.create.validation.companyNameRequired');
    }

    if (!formData.contactPrimaryEmail.trim()) {
      newErrors.contactPrimaryEmail = translate('dashboard.companies.modal.create.validation.emailRequired');
    } else if (!validateEmail(formData.contactPrimaryEmail)) {
      newErrors.contactPrimaryEmail = translate('dashboard.companies.modal.create.validation.emailInvalid');
    }

    if (!formData.contactPrimaryFirstName.trim()) {
      newErrors.contactPrimaryFirstName = translate('dashboard.companies.modal.create.validation.firstNameRequired');
    }

    if (!formData.contactPrimaryLastName.trim()) {
      newErrors.contactPrimaryLastName = translate('dashboard.companies.modal.create.validation.lastNameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    
    try {
      // Convert logo file to base64 if provided
      let logoBase64 = null;
      if (logoFile) {
        logoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            // Remove data:image/...;base64, prefix
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });
      }

      const dataToSend = {
        companyCode: formData.companyCode.trim(),
        companyName: formData.companyName.trim(),
        contactPrimaryEmail: formData.contactPrimaryEmail.trim(),
        contactPrimaryFirstName: formData.contactPrimaryFirstName.trim(),
        contactPrimaryLastName: formData.contactPrimaryLastName.trim(),
        lockerConsolidation: formData.lockerConsolidation,
        ...(logoBase64 && { logoBase64 })
      };

      const result = await onCreateSuccess(dataToSend);
      
      if (result.success) {
        // Reset form and close modal
        handleClose();
      } else {
        setErrors({ submit: result.error || translate('dashboard.companies.modal.create.errors.createError') });
      }
    } catch (error) {
      setErrors({ submit: error.message || translate('dashboard.companies.modal.create.errors.createError') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      // Reset form
      setFormData({
        companyCode: '',
        companyName: '',
        contactPrimaryEmail: '',
        contactPrimaryFirstName: '',
        contactPrimaryLastName: '',
        lockerConsolidation: false
      });
      setLogoFile(null);
      setLogoPreview(null);
      setErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('dashboard.companies.modal.create.title')}
      size="lg"
      closeOnBackdropClick={!isSaving}
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Logo Upload Section */}
        <div className={styles.logoSection}>
          <label className={styles.sectionLabel}>
            <FiImage size={18} />
            <span>{translate('dashboard.companies.modal.create.sections.logo.title')}</span>
          </label>
          <div className={styles.logoUploadContainer}>
            {logoPreview ? (
              <div className={styles.logoPreviewWrapper}>
                <img 
                  src={logoPreview}
                  alt={translate('dashboard.companies.modal.create.logo.previewAlt')}
                  className={styles.logoPreview}
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className={styles.removeLogoButton}
                  disabled={isSaving}
                >
                  <FaIcons.FaTimes />
                </button>
              </div>
            ) : (
              <div className={styles.logoPlaceholder}>
                <FiImage size={32} />
              </div>
            )}
            <div className={styles.logoActions}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleFileSelect}
                className={styles.fileInput}
                id="logo-upload"
                disabled={isSaving}
              />
              <label 
                htmlFor="logo-upload" 
                className={styles.uploadLabel}
              >
                <FiUpload size={16} />
                <span>{logoFile ? translate('dashboard.companies.modal.create.logo.changeButton') : translate('dashboard.companies.modal.create.logo.selectButton')}</span>
              </label>
              <span className={styles.logoInfo}>{translate('dashboard.companies.modal.create.logo.fileInfo')}</span>
            </div>
            {errors.logo && (
              <span className={styles.errorText}>{errors.logo}</span>
            )}
          </div>
        </div>

        {/* Company Information */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>
            <FiBriefcase size={18} />
            <span>{translate('dashboard.companies.modal.create.sections.companyInfo.title')}</span>
          </label>
          <div className={styles.row}>
            <Input
              label={translate('dashboard.companies.modal.create.fields.companyCode.label')}
              placeholder={translate('dashboard.companies.modal.create.fields.companyCode.placeholder')}
              value={formData.companyCode}
              onChange={(e) => handleInputChange('companyCode', e.target.value)}
              error={errors.companyCode}
              required
              fullWidth
              disabled={isSaving}
            />
          </div>
          <div className={styles.row}>
            <Input
              label={translate('dashboard.companies.modal.create.fields.companyName.label')}
              placeholder={translate('dashboard.companies.modal.create.fields.companyName.placeholder')}
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              error={errors.companyName}
              required
              fullWidth
              disabled={isSaving}
            />
          </div>
          <div className={styles.row}>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                id="lockerConsolidation"
                checked={formData.lockerConsolidation}
                onChange={(e) => handleInputChange('lockerConsolidation', e.target.checked)}
                disabled={isSaving}
                className={styles.checkbox}
              />
              <label htmlFor="lockerConsolidation" className={styles.checkboxLabel}>
                {translate('dashboard.companies.modal.create.fields.lockerConsolidation.label')}
              </label>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>
            <FiUsers size={18} />
            <span>{translate('dashboard.companies.modal.create.sections.contactInfo.title')}</span>
          </label>
          <div className={styles.row}>
            <Input
              label={translate('dashboard.companies.modal.create.fields.firstName.label')}
              placeholder={translate('dashboard.companies.modal.create.fields.firstName.placeholder')}
              value={formData.contactPrimaryFirstName}
              onChange={(e) => handleInputChange('contactPrimaryFirstName', e.target.value)}
              error={errors.contactPrimaryFirstName}
              required
              fullWidth
              disabled={isSaving}
            />
          </div>
          <div className={styles.row}>
            <Input
              label={translate('dashboard.companies.modal.create.fields.lastName.label')}
              placeholder={translate('dashboard.companies.modal.create.fields.lastName.placeholder')}
              value={formData.contactPrimaryLastName}
              onChange={(e) => handleInputChange('contactPrimaryLastName', e.target.value)}
              error={errors.contactPrimaryLastName}
              required
              fullWidth
              disabled={isSaving}
            />
          </div>
          <div className={styles.row}>
            <Input
              type="email"
              label={translate('dashboard.companies.modal.create.fields.email.label')}
              placeholder={translate('dashboard.companies.modal.create.fields.email.placeholder')}
              value={formData.contactPrimaryEmail}
              onChange={(e) => handleInputChange('contactPrimaryEmail', e.target.value)}
              error={errors.contactPrimaryEmail}
              required
              fullWidth
              disabled={isSaving}
            />
          </div>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className={styles.errorMessage}>
            <FaIcons.FaExclamationCircle />
            <span>{errors.submit}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles.actions}>
          <Button
            type="button"
            variant="ghostOutline"
            onClick={handleClose}
            disabled={isSaving}
            fullWidth
          >
            {translate('global.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSaving}
            disabled={isSaving}
            fullWidth
            leftIcon={<FaIcons.FaPlus />}
          >
            {translate('dashboard.companies.modal.create.actions.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateCompanyModal;

